// My Dashboard › Evaluation Accountability. The person's own obligations in evaluation
// processes, shown apart from the CVI contribution results: it is not a CVI pillar and
// not a score. Data come from the same endpoint and hook as the Work Center
// "Evaluation duties" tab, so both surfaces show the same duties with the same rules.
import { useState } from "react";
import UiIcon from "../components/UiIcon";
import { DetailDrawer } from "./WorkspaceUi";
import { EvaluationDutiesView, useEvaluationDuties } from "./EvaluationDuties";
import { formatNumber, formatPercent } from "./copy";
import { evaluationResponsibilityCopy, formatErDate } from "../components/routine/evaluationResponsibilityCopy";

const OPEN_STATES = ["OVERDUE", "DUE_SOON", "OPEN"];

// Everything below is read from the backend summary; nothing is recomputed here.
// Rates and "not applicable" exist only for a verified period, and an empty, unverified
// registry is never presented as "no duties".
export function accountabilityView(data, { error = false } = {}) {
    if (error) return { kind: "error" };
    if (!data) return { kind: "loading" };
    if (!data.period) return { kind: "no_period" };
    const verified = data.certification?.status === "VERIFIED";
    const counts = data.summary?.counts || null;
    const items = data.items || [];
    const base = { verified, counts, items, reason: data.certification?.reason || null,
        next: items.find(item => OPEN_STATES.includes(item.state)) || null };
    if (verified && data.summary?.applicability === "NOT_APPLICABLE") return { ...base, kind: "not_applicable" };
    if (verified && data.summary?.applicability === "EXEMPT") return { ...base, kind: "exempt" };
    if (!verified && !items.length) return { ...base, kind: "pending_empty" };
    return { ...base, kind: verified ? "verified" : "provisional", onTime: verified ? data.summary?.onTime || null : null };
}

const BADGE_TONE = { verified: "success", provisional: "info", pending_empty: "info", not_applicable: "neutral", exempt: "neutral", error: "danger" };

export default function EvaluationAccountabilitySection({ language = "en", periodId, periodLabel, navigator }) {
    const c = evaluationResponsibilityCopy(language);
    const t = c.dashboard;
    const duties = useEvaluationDuties(periodId, Boolean(periodId));
    const [open, setOpen] = useState(false);
    const view = accountabilityView(duties.data, { error: duties.error });
    const { counts } = view;
    const number = value => formatNumber(value ?? 0, language);
    const hasDetails = Boolean(view.items?.length);

    let body;
    if (view.kind === "loading") body = <div className="cvi-xp-skeleton" aria-busy="true"><span className="cvi-xp-sr">{c.loading}</span><i /></div>;
    else if (view.kind === "error") body = <div className="cvi-xp-empty is-error" role="alert"><span>{t.error}</span>
        <button type="button" className="cvi-xp-link" onClick={duties.reload}>{c.retry}</button></div>;
    else if (view.kind === "no_period") body = <div className="cvi-xp-empty"><small>{t.noPeriod}</small></div>;
    else if (["pending_empty", "not_applicable", "exempt"].includes(view.kind)) {
        const [title, help] = view.kind === "pending_empty" ? t.pendingEmpty : view.kind === "exempt" ? t.exempt : t.notApplicable;
        body = <div className={`cvi-xp-empty${view.kind === "pending_empty" ? "" : " is-calm"}`} role="status">
            {view.kind !== "pending_empty" && <UiIcon name="check" size={15} />}
            <div><strong>{title}</strong><small>{help}</small></div>
        </div>;
    } else body = <>
        {view.verified && <div className="cvi-er-dash-ontime">
            <div className="cvi-er-dash-ontime-head">
                <span>{t.onTimeTitle}</span>
                <strong>{view.onTime?.rate == null ? "—" : formatPercent(Math.round(view.onTime.rate), language)}</strong>
            </div>
            {view.onTime?.rate != null && <span className="cvi-xp-bar" role="img"
                aria-label={`${t.onTimeTitle}: ${formatPercent(Math.round(view.onTime.rate), language)}`}>
                <span style={{ width: `${Math.max(0, Math.min(100, view.onTime.rate))}%` }} /></span>}
            <small>{view.onTime?.due ? t.onTimeHint(number(view.onTime.onTime), number(view.onTime.due)) : t.onTimeNone}</small>
        </div>}
        <dl className="cvi-er-dash-counts">
            <div><dt>{t.assigned}</dt><dd>{number(counts.assigned)}</dd><small>{t.assignedHint(number(counts.open))}</small></div>
            <div><dt>{t.completed}</dt><dd>{number(counts.completed)}</dd><small>{t.completedHint(number(counts.onTime), counts.completedLate ? number(counts.completedLate) : 0, !counts.completed)}</small></div>
            <div className={counts.overdue ? "is-attention" : ""}><dt>{t.overdue}</dt><dd>{number(counts.overdue)}</dd><small>{t.overdueHint(counts.dueSoon ? number(counts.dueSoon) : 0)}</small></div>
        </dl>
        {view.next && <p className={`cvi-er-dash-next${view.next.state === "OVERDUE" ? " is-overdue" : ""}`}>
            <span>{view.next.state === "OVERDUE" ? t.nextOverdue : t.nextDue}</span>
            {[c.sources[view.next.sourceType] || view.next.sourceType, view.next.label].filter(Boolean).join(" · ")}
            <time dateTime={view.next.dueAt}> · {formatErDate(view.next.dueAt, language)}</time>
        </p>}
        {(counts.transferred > 0 || counts.exempt > 0) && <p className="cvi-er-dash-closed">{t.closed(counts.transferred, counts.exempt)} · {t.closedNote}</p>}
        {!view.verified && <p className="cvi-er-dash-provisional" role="note"><UiIcon name="shield" size={14} />{t.provisional[view.reason] || t.provisional.RECORDS_INCOMPLETE}</p>}
    </>;

    return <section className="cvi-manager-attention-card cvi-xp-card cvi-er-dash" aria-labelledby="er-dash-title" data-cvi-i18n="managed">
        <div className="cvi-manager-section-head cvi-xp-head cvi-er-dash-head">
            <div>
                <span>{t.eyebrow}</span>
                <h2 id="er-dash-title">{t.title}</h2>
                <p>{t.subtitle}</p>
            </div>
            {!["loading", "no_period"].includes(view.kind) && <span className={`cvi-er-dash-badge is-${BADGE_TONE[view.kind]}`}>{t.status[view.kind]}</span>}
        </div>
        {body}
        {hasDetails && <div className="cvi-er-dash-actions">
            <button type="button" className="cvi-xp-link" onClick={() => setOpen(true)} aria-haspopup="dialog">{t.details}<UiIcon name="chevronRight" size={14} /></button>
        </div>}
        {open && <DetailDrawer open onClose={() => setOpen(false)} language={language} width="md" title={t.title}
            headerExtra={<span className="cvi-ws-wc-summary">{t.drawerSubtitle(duties.data?.period?.name || periodLabel || "")}</span>}>
            <EvaluationDutiesView language={language} data={duties.data} error={duties.error} onRetry={duties.reload}
                navigator={navigator} onNavigate={() => setOpen(false)} />
        </DetailDrawer>}
    </section>;
}
